import { ArrowDown, ArrowUp, Info } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  Scatter,
  ScatterChart,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

import type {
  CampaignAnalysisOverview,
  CampaignAnalysisPerformanceQuadrant,
  CampaignAnalysisPostDistributionGroup,
} from '@/entities/campaign/generated/campaign-type';
import {
  compositionChartColors,
  formatCompactNumber,
  formatWholeNumber,
} from '../../helpers/analysis-report';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/shared/components/shadcn/chart';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils';

type AnalysisStat = {
  label: string;
  value: number;
  displayValue?: string;
  helper?: string;
  inlineDetail?: string;
  baselineComparison?: {
    baselineDisplayValue: string;
    direction: 'above' | 'below';
    ratioDisplayValue: string;
    tooltip: string;
  };
};

type PostDistributionGroup = CampaignAnalysisPostDistributionGroup;

export function OverviewSection({
  mode = 'screen',
  overview,
}: {
  mode?: 'print' | 'screen';
  overview: CampaignAnalysisOverview;
}) {
  const primaryEngagementRate = overview.aggregate.primary_average_engagement_rate;
  const baselineEngagementRate =
    overview.aggregate.baseline_recent_k_posts_average_engagement_rate;
  const engagementRateRatio =
    overview.aggregate.primary_average_engagement_rate_ratio_to_baseline;
  const primaryAggregateStats: AnalysisStat[] = [
    {
      label: 'Average view',
      value: overview.aggregate.primary_average_views,
      displayValue: formatCompactNumber(overview.aggregate.primary_average_views),
      baselineComparison: getNumberBaselineComparison({
        baseline: overview.aggregate.baseline_recent_k_posts_average_views,
        current: overview.aggregate.primary_average_views,
        formatBaseline: formatCompactNumber,
        tooltip: 'Compared with the recent-post baseline for analyzed creators.',
      }),
    },
    ...(primaryEngagementRate === null
      ? []
      : [
          {
            label: 'Average ER',
            value: primaryEngagementRate,
            displayValue: formatPercent(primaryEngagementRate),
            baselineComparison:
              baselineEngagementRate === null || engagementRateRatio === null
                ? undefined
                : getPercentBaselineComparison({
                    baseline: baselineEngagementRate,
                    current: primaryEngagementRate,
                    ratio: engagementRateRatio,
                    tooltip: 'Compared with the recent-post baseline for analyzed creators.',
                  }),
          },
        ]),
  ];
  const participationStats: AnalysisStat[] = [
    {
      label: 'Total posts',
      value: overview.participation.total_post_count,
      displayValue: formatWholeNumber(overview.participation.total_post_count),
    },
    {
      label: 'Participating creators',
      value: overview.participation.participating_creator_count,
      displayValue: formatWholeNumber(overview.participation.participating_creator_count),
    },
  ];
  const aggregateStats: AnalysisStat[] = [
    {
      label: 'Total views',
      value: overview.aggregate.total_views,
      displayValue: formatCompactNumber(overview.aggregate.total_views),
    },
    {
      label: 'Total likes',
      value: overview.aggregate.total_likes,
      displayValue: formatCompactNumber(overview.aggregate.total_likes),
    },
    {
      label: 'Total comments',
      value: overview.aggregate.total_comments,
      displayValue: formatCompactNumber(overview.aggregate.total_comments),
    },
    {
      label: 'Total shares',
      value: overview.aggregate.total_shares,
      displayValue: formatCompactNumber(overview.aggregate.total_shares),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <OverviewCard title="Participation" tone="muted">
        <StatGrid stats={participationStats} columnsClassName="lg:grid-cols-2" />
      </OverviewCard>

      <OverviewCard title="Aggregate">
        <div className="grid gap-6">
          <StatGrid
            mode={mode}
            stats={primaryAggregateStats}
            columnsClassName="lg:grid-cols-2"
            variant="primary"
          />
          <PerformanceQuadrantChart mode={mode} quadrant={overview.performance_quadrant} />
          <div className="border-border border-t pt-5">
            <StatGrid stats={aggregateStats} columnsClassName="lg:grid-cols-4" />
          </div>
        </div>
      </OverviewCard>

      <OverviewCard
        title="Creator composition"
        description="Distribution is based on post count, not unique creator count."
      >
        <CreatorCompositionPanel composition={overview.creator_composition} />
      </OverviewCard>
    </div>
  );
}

function OverviewCard({
  children,
  className,
  description,
  title,
  tone = 'default',
}: {
  children: ReactNode;
  className?: string;
  description?: string;
  title: string;
  tone?: 'default' | 'muted';
}) {
  return (
    <div
      data-campaign-analysis-print-avoid-break
      className={cn(
        'border-border rounded-xl border p-5 shadow-sm sm:p-6 print:p-5',
        tone === 'muted' ? 'bg-muted' : 'bg-card',
        className,
      )}
    >
      <div className="max-w-3xl">
        <p className="text-foreground text-lg leading-6 font-semibold break-words">{title}</p>
        {description ? (
          <p className="text-muted-foreground mt-1.5 text-sm leading-5">{description}</p>
        ) : null}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function getNumberBaselineComparison({
  baseline,
  current,
  formatBaseline,
  tooltip,
}: {
  baseline: number;
  current: number;
  formatBaseline: (value: number) => string;
  tooltip: string;
}) {
  if (!Number.isFinite(current) || !Number.isFinite(baseline)) return undefined;

  const ratioDisplayValue = getBaselineRatioDisplayValue(current, baseline);

  if (!ratioDisplayValue) return undefined;

  return {
    direction: getBaselineDirection(current, baseline),
    baselineDisplayValue: formatBaseline(baseline),
    ratioDisplayValue,
    tooltip,
  } satisfies NonNullable<AnalysisStat['baselineComparison']>;
}

function getPercentBaselineComparison({
  baseline,
  current,
  ratio,
  tooltip,
}: {
  baseline: number;
  current: number;
  ratio: number;
  tooltip: string;
}) {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || !Number.isFinite(ratio)) {
    return undefined;
  }

  return {
    direction: getBaselineDirection(current, baseline),
    baselineDisplayValue: formatPercent(baseline),
    ratioDisplayValue: `${ratio.toFixed(2)}x`,
    tooltip,
  } satisfies NonNullable<AnalysisStat['baselineComparison']>;
}

function getBaselineDirection(current: number, baseline: number) {
  return current >= baseline ? 'above' : 'below';
}

function getBaselineRatioDisplayValue(current: number, baseline: number) {
  if (!Number.isFinite(current) || !Number.isFinite(baseline) || baseline <= 0) return null;

  return `${(current / baseline).toFixed(2)}x`;
}

function StatGrid({
  columnsClassName,
  mode = 'screen',
  stats,
  variant = 'default',
}: {
  columnsClassName: string;
  mode?: 'print' | 'screen';
  stats: readonly AnalysisStat[];
  variant?: 'default' | 'primary';
}) {
  return (
    <div className={cn('grid gap-x-5 gap-y-5 sm:grid-cols-2 print:grid-cols-2', columnsClassName)}>
      {stats.map((stat) => (
        <div key={stat.label} className="min-w-0">
          <p className="text-muted-foreground text-sm leading-5 font-medium">{stat.label}</p>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <p
              className={cn(
                'text-foreground font-bold tracking-tight break-words',
                variant === 'primary' ? 'text-3xl leading-9' : 'text-2xl leading-7',
              )}
            >
              {getAnalysisStatDisplayValue(stat)}
            </p>
            {stat.inlineDetail ? (
              <p className="text-muted-foreground text-sm leading-5 font-medium">
                {stat.inlineDetail}
              </p>
            ) : null}
          </div>
          {stat.baselineComparison ? (
            <BaselineComparison comparison={stat.baselineComparison} mode={mode} />
          ) : null}
          {stat.helper ? (
            <p className="text-muted-foreground mt-1.5 text-sm leading-5">{stat.helper}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function BaselineComparison({
  comparison,
  mode = 'screen',
}: {
  comparison: NonNullable<AnalysisStat['baselineComparison']>;
  mode?: 'print' | 'screen';
}) {
  const isAboveBaseline = comparison.direction === 'above';
  const Icon = isAboveBaseline ? ArrowUp : ArrowDown;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm leading-5">
      <span
        className={cn(
          'inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs leading-none font-semibold',
          isAboveBaseline ? 'bg-chart-1/10 text-chart-1' : 'bg-destructive/10 text-destructive',
        )}
      >
        <Icon aria-hidden="true" className="size-3.5" />
        {isAboveBaseline ? 'Above' : 'Below'} {comparison.ratioDisplayValue}
      </span>
      <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1 font-medium">
        <span className="break-words">vs baseline {comparison.baselineDisplayValue}</span>
        {mode === 'screen' ? (
          <TooltipFlat
            asChild
            side="top"
            contentClassName="max-w-64 text-sm leading-5"
            content={comparison.tooltip}
          >
            <span
              tabIndex={0}
              aria-label={comparison.tooltip}
              className="hover:text-foreground focus-visible:ring-ring/50 inline-flex size-4 shrink-0 cursor-help items-center justify-center rounded-full transition-colors outline-none focus-visible:ring-2"
            >
              <Info aria-hidden="true" className="size-3.5" />
            </span>
          </TooltipFlat>
        ) : (
          <span
            aria-label={comparison.tooltip}
            className="inline-flex size-4 shrink-0 items-center justify-center rounded-full"
          >
            <Info aria-hidden="true" className="size-3.5" />
          </span>
        )}
      </span>
    </div>
  );
}

type PerformanceQuadrant = CampaignAnalysisPerformanceQuadrant;
type NormalizedPerformanceQuadrant = Omit<PerformanceQuadrant, 'y_baseline_value'> & {
  y_baseline_value: number;
};

type PerformanceQuadrantKey = 'highHigh' | 'lowHigh' | 'highLow' | 'lowLow';

type PerformanceQuadrantChartPoint = PerformanceQuadrant['points'][number] & {
  pointSize: number;
  quadrantKey: PerformanceQuadrantKey;
  viewsPosition: number;
};

const performanceQuadrantDefinitions = {
  highHigh: {
    chartLabel: 'High views, High ER',
    color: 'var(--chart-1)',
    label: 'High views, High ER',
  },
  lowHigh: {
    chartLabel: 'Low views, High ER',
    color: 'var(--chart-2)',
    label: 'Low views, High ER',
  },
  highLow: {
    chartLabel: 'High views, Low ER',
    color: 'var(--chart-4)',
    label: 'High views, Low ER',
  },
  lowLow: {
    chartLabel: 'Low views, Low ER',
    color: 'var(--muted-foreground)',
    label: 'Low views, Low ER',
  },
} satisfies Record<PerformanceQuadrantKey, { chartLabel: string; color: string; label: string }>;

const performanceQuadrantChartConfig = Object.fromEntries(
  Object.entries(performanceQuadrantDefinitions).map(([key, value]) => [
    key,
    {
      color: value.color,
      label: value.chartLabel,
    },
  ]),
) satisfies ChartConfig;

const percentFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  style: 'percent',
});
const performanceQuadrantXAxisTicks = [0, 100, 1_000, 100_000] as const;
const performanceQuadrantXAxisTickPositions = performanceQuadrantXAxisTicks.map(
  (_tick, index) => index,
);
const performanceQuadrantXDomain: [number, number] = [0, performanceQuadrantXAxisTicks.length - 1];
const performanceQuadrantYAxisTicks = [0.1, 0.2, 0.3] as const;

function PerformanceQuadrantChart({
  mode = 'screen',
  quadrant,
}: {
  mode?: 'print' | 'screen';
  quadrant: PerformanceQuadrant;
}) {
  const normalizedQuadrant: NormalizedPerformanceQuadrant = {
    ...quadrant,
    y_baseline_value: quadrant.y_baseline_value ?? 0,
  };
  const chartPoints = getPerformanceQuadrantPoints(normalizedQuadrant);

  if (
    !chartPoints.length ||
    !Number.isFinite(normalizedQuadrant.x_baseline_value) ||
    !Number.isFinite(normalizedQuadrant.y_baseline_value)
  ) {
    return (
      <section>
        <p className="text-foreground text-sm leading-5 font-semibold">Post performance map</p>
        <div className="border-border bg-background text-muted-foreground mt-3 rounded-lg border px-4 py-8 text-center text-sm leading-5">
          No post performance data
        </div>
      </section>
    );
  }

  const yDomain = getPerformanceQuadrantDomain({
    baselineValue: normalizedQuadrant.y_baseline_value,
    minValue: 0,
    points: chartPoints.map((point) => point.y),
    tickValues: performanceQuadrantYAxisTicks,
  });
  const groupedPoints = getGroupedPerformanceQuadrantPoints(chartPoints);
  const xBaselinePosition = getPerformanceQuadrantXPosition(normalizedQuadrant.x_baseline_value);
  const pointSize = getPerformanceQuadrantPointSize(chartPoints.length);

  if (mode === 'print') {
    return (
      <PerformanceQuadrantStaticChart
        groupedPoints={groupedPoints}
        pointSize={pointSize}
        quadrant={normalizedQuadrant}
        xBaselinePosition={xBaselinePosition}
        yDomain={yDomain}
      />
    );
  }

  return (
    <section>
      <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm leading-5">
        <p>
          <span className="font-medium">Post performance map</span> (Views X ER by Post)
        </p>
      </div>

      <ChartContainer
        config={performanceQuadrantChartConfig}
        className="mt-3 aspect-auto h-80 w-full"
      >
        <ScatterChart accessibilityLayer margin={{ bottom: 16, left: 8, right: 16, top: 16 }}>
          <CartesianGrid strokeDasharray="4 4" />
          <ReferenceArea
            x1={xBaselinePosition}
            x2={performanceQuadrantXDomain[1]}
            y1={normalizedQuadrant.y_baseline_value}
            y2={yDomain[1]}
            fill={performanceQuadrantDefinitions.highHigh.color}
            fillOpacity={0.08}
          />
          <ReferenceArea
            x1={performanceQuadrantXDomain[0]}
            x2={xBaselinePosition}
            y1={normalizedQuadrant.y_baseline_value}
            y2={yDomain[1]}
            fill={performanceQuadrantDefinitions.lowHigh.color}
            fillOpacity={0.08}
          />
          <ReferenceArea
            x1={xBaselinePosition}
            x2={performanceQuadrantXDomain[1]}
            y1={yDomain[0]}
            y2={normalizedQuadrant.y_baseline_value}
            fill={performanceQuadrantDefinitions.highLow.color}
            fillOpacity={0.08}
          />
          <ReferenceArea
            x1={performanceQuadrantXDomain[0]}
            x2={xBaselinePosition}
            y1={yDomain[0]}
            y2={normalizedQuadrant.y_baseline_value}
            fill={performanceQuadrantDefinitions.lowLow.color}
            fillOpacity={0.06}
          />
          <XAxis
            dataKey="viewsPosition"
            domain={performanceQuadrantXDomain}
            name="Views"
            ticks={performanceQuadrantXAxisTickPositions}
            tickFormatter={formatPerformanceQuadrantXAxisTick}
            tickLine={false}
            type="number"
          />
          <YAxis
            dataKey="y"
            domain={yDomain}
            name="ER"
            ticks={[...performanceQuadrantYAxisTicks]}
            tickFormatter={formatPercent}
            tickLine={false}
            type="number"
            width={48}
          />
          <ZAxis dataKey="pointSize" range={[pointSize, pointSize]} />
          <ReferenceLine
            x={xBaselinePosition}
            stroke="var(--muted-foreground)"
            strokeDasharray="4 4"
          />
          <ReferenceLine
            y={normalizedQuadrant.y_baseline_value}
            stroke="var(--muted-foreground)"
            strokeDasharray="4 4"
          />
          <ChartTooltip cursor={false} content={<PerformanceQuadrantTooltip />} />
          {Object.entries(groupedPoints).map(([quadrantKey, data]) => (
            <Scatter
              key={quadrantKey}
              data={data}
              fill={performanceQuadrantDefinitions[quadrantKey as PerformanceQuadrantKey].color}
              isAnimationActive={false}
              name={performanceQuadrantDefinitions[quadrantKey as PerformanceQuadrantKey].label}
            />
          ))}
        </ScatterChart>
      </ChartContainer>

      <div className="text-muted-foreground mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs leading-4 sm:pr-4 sm:pl-14">
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="border-muted-foreground/70 h-0 w-3.5 border-t border-dashed"
          />
          Dashed lines
        </span>
        <span>Median views {formatCompactNumber(normalizedQuadrant.x_baseline_value)}</span>
        <span>Median ER {formatPercent(normalizedQuadrant.y_baseline_value)}</span>
      </div>

      <PerformanceQuadrantLegend groupedPoints={groupedPoints} />
    </section>
  );
}

function PerformanceQuadrantStaticChart({
  groupedPoints,
  pointSize,
  quadrant,
  xBaselinePosition,
  yDomain,
}: {
  groupedPoints: Record<PerformanceQuadrantKey, readonly PerformanceQuadrantChartPoint[]>;
  pointSize: number;
  quadrant: NormalizedPerformanceQuadrant;
  xBaselinePosition: number;
  yDomain: [number, number];
}) {
  const chartWidth = 640;
  const chartHeight = 260;
  const plotLeft = 56;
  const plotRight = 20;
  const plotTop = 18;
  const plotBottom = 42;
  const tickFontSize = 4;
  const axisLabelFontSize = 5;
  const plotWidth = chartWidth - plotLeft - plotRight;
  const plotHeight = chartHeight - plotTop - plotBottom;
  const xBaseline = getStaticChartX({
    domain: performanceQuadrantXDomain,
    plotLeft,
    plotWidth,
    value: xBaselinePosition,
  });
  const yBaseline = getStaticChartY({
    domain: yDomain,
    plotHeight,
    plotTop,
    value: quadrant.y_baseline_value,
  });
  const staticPointRadius = Math.max(3, Math.min(6, pointSize / 6));
  const quadrantAreas = [
    {
      key: 'highHigh',
      x: xBaseline,
      y: plotTop,
      width: plotLeft + plotWidth - xBaseline,
      height: yBaseline - plotTop,
    },
    {
      key: 'lowHigh',
      x: plotLeft,
      y: plotTop,
      width: xBaseline - plotLeft,
      height: yBaseline - plotTop,
    },
    {
      key: 'highLow',
      x: xBaseline,
      y: yBaseline,
      width: plotLeft + plotWidth - xBaseline,
      height: plotTop + plotHeight - yBaseline,
    },
    {
      key: 'lowLow',
      x: plotLeft,
      y: yBaseline,
      width: xBaseline - plotLeft,
      height: plotTop + plotHeight - yBaseline,
    },
  ] satisfies Array<{
    height: number;
    key: PerformanceQuadrantKey;
    width: number;
    x: number;
    y: number;
  }>;

  return (
    <section>
      <div className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-sm leading-5">
        <p>
          <span className="font-medium">Post performance map</span> (Views X ER by Post)
        </p>
      </div>

      <div
        data-campaign-analysis-performance-chart-frame
        className="bg-background mt-3 overflow-hidden"
      >
        <svg
          data-campaign-analysis-performance-chart-svg
          role="img"
          aria-label="Post performance map by views and engagement rate"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="block h-auto w-full"
        >
          <rect width={chartWidth} height={chartHeight} fill="var(--background)" />
          {quadrantAreas.map((area) => (
            <rect
              key={area.key}
              x={area.x}
              y={area.y}
              width={Math.max(0, area.width)}
              height={Math.max(0, area.height)}
              fill={performanceQuadrantDefinitions[area.key].color}
              opacity={area.key === 'lowLow' ? 0.06 : 0.08}
            />
          ))}
          {performanceQuadrantYAxisTicks.map((tick) => {
            const y = getStaticChartY({
              domain: yDomain,
              plotHeight,
              plotTop,
              value: tick,
            });

            return (
              <g key={tick}>
                <line
                  x1={plotLeft}
                  x2={plotLeft + plotWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--border)"
                  strokeDasharray="4 4"
                />
                <text
                  data-campaign-analysis-performance-chart-tick
                  x={plotLeft - 10}
                  y={y + 1.5}
                  fill="var(--muted-foreground)"
                  fontSize={tickFontSize}
                  textAnchor="end"
                >
                  {formatPercent(tick)}
                </text>
              </g>
            );
          })}
          {performanceQuadrantXAxisTickPositions.map((position) => {
            const x = getStaticChartX({
              domain: performanceQuadrantXDomain,
              plotLeft,
              plotWidth,
              value: position,
            });

            return (
              <g key={position}>
                <line
                  x1={x}
                  x2={x}
                  y1={plotTop}
                  y2={plotTop + plotHeight}
                  stroke="var(--border)"
                  strokeDasharray="4 4"
                />
                <text
                  data-campaign-analysis-performance-chart-tick
                  x={x}
                  y={plotTop + plotHeight + 12}
                  fill="var(--muted-foreground)"
                  fontSize={tickFontSize}
                  textAnchor="middle"
                >
                  {formatPerformanceQuadrantXAxisTick(position)}
                </text>
              </g>
            );
          })}
          <line
            x1={xBaseline}
            x2={xBaseline}
            y1={plotTop}
            y2={plotTop + plotHeight}
            stroke="var(--muted-foreground)"
            strokeDasharray="5 5"
          />
          <line
            x1={plotLeft}
            x2={plotLeft + plotWidth}
            y1={yBaseline}
            y2={yBaseline}
            stroke="var(--muted-foreground)"
            strokeDasharray="5 5"
          />
          <line
            x1={plotLeft}
            x2={plotLeft}
            y1={plotTop}
            y2={plotTop + plotHeight}
            stroke="var(--border-strong)"
          />
          <line
            x1={plotLeft}
            x2={plotLeft + plotWidth}
            y1={plotTop + plotHeight}
            y2={plotTop + plotHeight}
            stroke="var(--border-strong)"
          />
          {Object.entries(groupedPoints).flatMap(([quadrantKey, points]) =>
            points.map((point, index) => (
              <circle
                key={`${quadrantKey}-${point.x}-${point.y}-${index}`}
                cx={getStaticChartX({
                  domain: performanceQuadrantXDomain,
                  plotLeft,
                  plotWidth,
                  value: point.viewsPosition,
                })}
                cy={getStaticChartY({
                  domain: yDomain,
                  plotHeight,
                  plotTop,
                  value: point.y,
                })}
                r={staticPointRadius}
                fill={performanceQuadrantDefinitions[quadrantKey as PerformanceQuadrantKey].color}
                opacity="0.78"
              />
            )),
          )}
          <text
            data-campaign-analysis-performance-chart-axis-label
            x={plotLeft + plotWidth / 2}
            y={chartHeight - 15}
            fill="var(--muted-foreground)"
            fontSize={axisLabelFontSize}
            textAnchor="middle"
          >
            Views
          </text>
          <text
            data-campaign-analysis-performance-chart-axis-label
            x={14}
            y={plotTop + plotHeight / 2}
            fill="var(--muted-foreground)"
            fontSize={axisLabelFontSize}
            textAnchor="middle"
            transform={`rotate(-90 14 ${plotTop + plotHeight / 2})`}
          >
            ER
          </text>
        </svg>
      </div>

      <div className="text-muted-foreground mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs leading-4 sm:pr-4 sm:pl-14">
        <span className="inline-flex items-center gap-2">
          <span
            aria-hidden="true"
            className="border-muted-foreground/70 h-0 w-3.5 border-t border-dashed"
          />
          Dashed lines
        </span>
        <span>Median views {formatCompactNumber(quadrant.x_baseline_value)}</span>
        <span>Median ER {formatPercent(quadrant.y_baseline_value)}</span>
      </div>

      <PerformanceQuadrantLegend groupedPoints={groupedPoints} />
    </section>
  );
}

function getStaticChartX({
  domain,
  plotLeft,
  plotWidth,
  value,
}: {
  domain: [number, number];
  plotLeft: number;
  plotWidth: number;
  value: number;
}) {
  return plotLeft + ((value - domain[0]) / (domain[1] - domain[0])) * plotWidth;
}

function getStaticChartY({
  domain,
  plotHeight,
  plotTop,
  value,
}: {
  domain: [number, number];
  plotHeight: number;
  plotTop: number;
  value: number;
}) {
  return plotTop + (1 - (value - domain[0]) / (domain[1] - domain[0])) * plotHeight;
}

function PerformanceQuadrantTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: PerformanceQuadrantChartPoint }>;
}) {
  const point = payload?.[0]?.payload;

  if (!active || !point) return null;

  const quadrant = performanceQuadrantDefinitions[point.quadrantKey];

  return (
    <div className="border-border/50 bg-background grid min-w-40 gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <p className="text-foreground font-medium">{quadrant.label}</p>
      <div className="text-muted-foreground grid gap-1">
        <p>Views {formatCompactNumber(point.x)}</p>
        <p>ER {formatPercent(point.y)}</p>
      </div>
    </div>
  );
}

function PerformanceQuadrantLegend({
  groupedPoints,
}: {
  groupedPoints: Record<PerformanceQuadrantKey, readonly PerformanceQuadrantChartPoint[]>;
}) {
  return (
    <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-2 sm:pr-4 sm:pl-14">
      {Object.entries(performanceQuadrantDefinitions).map(([quadrantKey, definition]) => (
        <div
          key={quadrantKey}
          className="text-muted-foreground inline-flex items-center gap-2 text-xs leading-4"
        >
          <span
            aria-hidden="true"
            className="size-2.5 rounded-sm"
            style={{ backgroundColor: definition.color }}
          />
          <span className="font-medium">{definition.label}</span>
          <span className="text-foreground font-semibold">
            {formatWholeNumber(groupedPoints[quadrantKey as PerformanceQuadrantKey].length)}
          </span>
        </div>
      ))}
    </div>
  );
}

function getPerformanceQuadrantPoints(quadrant: NormalizedPerformanceQuadrant) {
  const validPoints = quadrant.points.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y) && point.x >= 0 && point.y >= 0,
  );
  const pointSize = getPerformanceQuadrantPointSize(validPoints.length);

  return validPoints.map((point) => ({
    ...point,
    pointSize,
    quadrantKey: getPerformanceQuadrantKey(point, quadrant),
    viewsPosition: getPerformanceQuadrantXPosition(point.x),
  }));
}

function getGroupedPerformanceQuadrantPoints(points: readonly PerformanceQuadrantChartPoint[]) {
  return {
    highHigh: points.filter((point) => point.quadrantKey === 'highHigh'),
    lowHigh: points.filter((point) => point.quadrantKey === 'lowHigh'),
    highLow: points.filter((point) => point.quadrantKey === 'highLow'),
    lowLow: points.filter((point) => point.quadrantKey === 'lowLow'),
  } satisfies Record<PerformanceQuadrantKey, PerformanceQuadrantChartPoint[]>;
}

function getPerformanceQuadrantKey(
  point: PerformanceQuadrant['points'][number],
  quadrant: NormalizedPerformanceQuadrant,
): PerformanceQuadrantKey {
  const highViews = point.x >= quadrant.x_baseline_value;
  const highEngagement = point.y >= quadrant.y_baseline_value;

  if (highViews && highEngagement) return 'highHigh';
  if (!highViews && highEngagement) return 'lowHigh';
  if (highViews && !highEngagement) return 'highLow';
  return 'lowLow';
}

function getPerformanceQuadrantPointSize(pointCount: number) {
  if (pointCount >= 300) return 10;
  if (pointCount >= 200) return 16;
  if (pointCount >= 100) return 28;
  if (pointCount >= 50) return 44;
  return 64;
}

function getPerformanceQuadrantDomain({
  baselineValue,
  minValue = 0,
  points,
  tickValues,
}: {
  baselineValue: number;
  minValue?: number;
  points: readonly number[];
  tickValues: readonly number[];
}) {
  const maxTickValue = Math.max(...tickValues);
  const maxPointValue = Math.max(...points.filter(Number.isFinite), baselineValue, maxTickValue);

  return [minValue, maxPointValue] as [number, number];
}

function getPerformanceQuadrantXPosition(value: number) {
  if (!Number.isFinite(value) || value <= performanceQuadrantXAxisTicks[0]) return 0;

  for (let index = 1; index < performanceQuadrantXAxisTicks.length; index += 1) {
    const previousTick = performanceQuadrantXAxisTicks[index - 1];
    const nextTick = performanceQuadrantXAxisTicks[index];

    if (value <= nextTick) {
      if (previousTick === 0) {
        return index - 1 + value / nextTick;
      }

      const segmentProgress =
        (Math.log10(value) - Math.log10(previousTick)) /
        (Math.log10(nextTick) - Math.log10(previousTick));

      return index - 1 + Math.max(0, Math.min(1, segmentProgress));
    }
  }

  return performanceQuadrantXAxisTicks.length - 1;
}

function formatPerformanceQuadrantXAxisTick(position: number) {
  const tick = performanceQuadrantXAxisTicks[position];

  return tick === undefined ? '' : formatCompactNumber(tick);
}

function formatPercent(value: number) {
  return percentFormatter.format(value);
}

function CreatorCompositionPanel({
  composition,
}: {
  composition: CampaignAnalysisOverview['creator_composition'];
}) {
  return (
    <div className="grid gap-x-16 gap-y-10 lg:grid-cols-2 print:grid-cols-2 print:gap-x-10 print:gap-y-8">
      <CompositionDonut title="Gender" groups={composition.gender_distribution} />
      <CompositionDonut title="Race" groups={composition.race_distribution} />
      <CompositionList title="Age" groups={composition.age_distribution} />
      <CompositionList title="Follower size" groups={composition.follower_size_distribution} />
    </div>
  );
}

function CompositionDonut({
  groups,
  title,
}: {
  groups: readonly PostDistributionGroup[];
  title: string;
}) {
  const topGroup = getTopDistributionGroup(groups);

  return (
    <section className="min-w-0">
      <p className="text-foreground text-sm leading-5 font-semibold">{title}</p>
      <div className="mt-4 grid items-center gap-5 sm:grid-cols-[132px_minmax(0,1fr)] print:grid-cols-[28mm_minmax(0,1fr)] print:gap-4">
        <div
          className="relative size-32 shrink-0 rounded-full print:size-28"
          role="img"
          aria-label={getCompositionAriaLabel(title, groups)}
          style={{ background: getCompositionConicGradient(groups) }}
        >
          <div className="bg-card border-border absolute inset-5 flex flex-col items-center justify-center rounded-full border text-center">
            <span className="text-foreground text-xl leading-6 font-bold">
              {topGroup ? getDistributionDisplayValue(topGroup) : '-'}
            </span>
            <span className="text-muted-foreground mt-0.5 max-w-16 text-xs leading-4 break-words">
              {topGroup?.label ?? 'No data'}
            </span>
          </div>
        </div>

        <DistributionLegend groups={groups} />
      </div>
    </section>
  );
}

function DistributionLegend({ groups }: { groups: readonly PostDistributionGroup[] }) {
  return (
    <div className="grid gap-2.5">
      {groups.map((group, index) => {
        const displayValue = getDistributionDisplayValue(group);

        return (
          <div
            key={group.label}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-2 text-sm leading-5"
          >
            <span
              className="mt-1.5 size-2.5 rounded-full"
              style={{ backgroundColor: getCompositionColor(index) }}
              aria-hidden="true"
            />
            <div className="min-w-0">
              <p className="text-foreground font-medium break-words">{group.label}</p>
              <p className="text-muted-foreground text-xs leading-4">
                {formatWholeNumber(group.post_count)} posts
              </p>
            </div>
            <span className="text-muted-foreground shrink-0 font-medium">{displayValue}</span>
          </div>
        );
      })}
    </div>
  );
}

function CompositionList({
  groups,
  title,
}: {
  groups: readonly PostDistributionGroup[];
  title: string;
}) {
  const primaryValue = Math.max(...groups.map((group) => group.proportion));

  return (
    <div className="min-w-0">
      <p className="text-foreground text-sm leading-5 font-semibold">{title}</p>
      <div className="mt-3 flex flex-col gap-3.5">
        {groups.map((group) => {
          const safeValue = Math.max(0, Math.min(100, group.proportion * 100));
          const displayValue = getDistributionDisplayValue(group);
          const isPrimaryGroup = group.proportion === primaryValue;

          return (
            <div key={group.label} className="grid gap-1.5">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 text-sm leading-5">
                <div className="min-w-0">
                  <p className="text-foreground font-medium break-words">{group.label}</p>
                  <p className="text-muted-foreground text-xs leading-4">
                    {formatWholeNumber(group.post_count)} posts
                  </p>
                </div>
                <span className="text-muted-foreground shrink-0 font-medium">{displayValue}</span>
              </div>
              <div className="bg-muted h-2 overflow-hidden rounded-full border">
                <div
                  className={cn(
                    'h-full rounded-full',
                    isPrimaryGroup ? 'bg-primary' : 'bg-muted-foreground/35',
                  )}
                  style={{ width: `${safeValue}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getCompositionConicGradient(
  groups: readonly PostDistributionGroup[],
): CSSProperties['background'] {
  const totalValue = groups.reduce((sum, group) => sum + Math.max(0, group.proportion), 0);

  if (totalValue <= 0) {
    return 'var(--muted)';
  }

  let start = 0;
  const segments = groups.map((group, index) => {
    const end = start + (Math.max(0, group.proportion) / totalValue) * 100;
    const segment = `${getCompositionColor(index)} ${start}% ${end}%`;
    start = end;
    return segment;
  });

  return `conic-gradient(${segments.join(', ')})`;
}

function getCompositionColor(index: number) {
  return compositionChartColors[index % compositionChartColors.length];
}

function getDistributionDisplayValue(group: PostDistributionGroup) {
  return formatPercent(group.proportion);
}

function getTopDistributionGroup(groups: readonly PostDistributionGroup[]) {
  return groups.reduce<PostDistributionGroup | null>((topGroup, group) => {
    if (!topGroup || group.proportion > topGroup.proportion) {
      return group;
    }

    return topGroup;
  }, null);
}

function getCompositionAriaLabel(title: string, groups: readonly PostDistributionGroup[]) {
  const groupLabels = groups
    .map((group) => `${group.label} ${getDistributionDisplayValue(group)}`)
    .join(', ');

  return `${title} distribution by post count: ${groupLabels}`;
}

function getAnalysisStatDisplayValue(stat: AnalysisStat) {
  return stat.displayValue ?? String(stat.value);
}
